import { CircleLayer, SymbolLayer } from '@trimblemaps/trimblemaps-js';
import { Theme } from '@mui/material';
import { FeatureTypes, MapLayers, MapSources } from '../enums';
import { isVisibleUtil } from '../mapUtils';

/**
 * Creates a layer to display circles on the map for each Point and 'featureType' of 'orderOriginCenter' in the given map source.
 * Adds the shipper details center layer to the map if it doesn't already exist
 * @param map Instance of your map
 * @param mapSource Name of the map source
 * @param isVisible Indicator if the map layer is visible
 */
export const addShipperDetailsCenterLayer = ({
    map,
    mapSource,
    isVisible,
}: {
    map: TrimbleMaps.Map;
    mapSource: MapSources;
    isVisible: boolean;
}): void => {
    const visibility = isVisibleUtil(isVisible);

    const layer: CircleLayer = {
        id: MapLayers.shipperDetailsCenterLayer,
        type: 'circle',
        source: mapSource,
        layout: {
            visibility,
        },
        paint: {
            'circle-color': ['get', 'color'],
            'circle-opacity': 1.0,
            'circle-radius': 4,
            'circle-stroke-color': ['get', 'strokeColor'],
            'circle-stroke-width': 2,
        },
        filter: ['all',
            ['==', '$type', 'Point'],
            ['==', 'featureType', FeatureTypes.OrderOriginCenter],
            ['!has', 'point_count'],
        ],
    };

    if (map.getLayer(MapLayers.shipperDetailsCenterLayer) === undefined) {
        map.addLayer(layer);
    }
};

/**
 * Creates a layer to display a cluster on the map for each Point in the given map source.
 * Adds the shipper details cluster layer to the map if it doesn't already exist
 * @param map Instance of your map
 * @param mapSource Name of the map source
 * @param isVisible Indicator if the map layer is visible
 */
export const addShipperDetailsClusterLayer = ({
    map,
    mapSource,
    theme,
    isVisible,
}: {
    map: TrimbleMaps.Map;
    mapSource: MapSources;
    theme: Theme;
    isVisible: boolean;
}): void => {
    const visibility = isVisibleUtil(isVisible);

    const layer: CircleLayer = {
        id: MapLayers.shipperDetailsClusterLayer,
        type: 'circle',
        source: mapSource,
        layout: {
            visibility,
        },
        paint: {
            'circle-radius': [
                'step',
                ['get', 'point_count'],
                15,
                5,
                20,
                50,
                25,
            ],
            'circle-color': theme.palette.secondary.main,
            'circle-stroke-color': theme.palette.info.main,
            'circle-stroke-width': 2,
        },
        filter: ['all',
            ['==', '$type', 'Point'],
            ['has', 'point_count'],
        ],
    };

    if (map.getLayer(MapLayers.shipperDetailsClusterLayer) === undefined) {
        map.addLayer(layer);
    }
};

/**
 * Creates a layer to display a cluster label on the map for each Point in the given map source.
 * Adds the shipper details cluster label layer to the map if it doesn't already exist
 * @param map Instance of your map
 * @param mapSource Name of the map source
 * @param isVisible Indicator if the map layer is visible
 */
export const addShipperDetailsClusterLabelLayer = ({
    map,
    mapSource,
    isVisible,
}: {
    map: TrimbleMaps.Map;
    mapSource: MapSources;
    isVisible: boolean;
}): void => {
    const visibility = isVisibleUtil(isVisible);

    const layer: SymbolLayer = {
        id: MapLayers.shipperDetailsClusterLabelLayer,
        type: 'symbol',
        source: mapSource,
        layout: {
            visibility,
            'text-field': '{point_count}',
            'text-font': ['Roboto Regular'],
            'text-size': 12,
        },
        paint: {
            'text-color': '#fff',
        },
        filter: ['all',
            ['==', '$type', 'Point'],
            ['has', 'point_count'],
        ],
    };

    if (map.getLayer(MapLayers.shipperDetailsClusterLabelLayer) === undefined) {
        map.addLayer(layer);
    }
};
